/**
 * FSRS — Free Spaced Repetition Scheduler.
 *
 * Implements the FSRS-4.5 algorithm: a memory model that tracks each item's
 * stability (how long until you'd forget) and difficulty (how hard the item
 * is for you personally). After every review, the algorithm updates these
 * based on a 4-point rating and schedules the next review at the time your
 * retention probability hits the desired threshold.
 *
 * Reference: https://github.com/open-spaced-repetition/fsrs4anki/wiki/The-Algorithm
 *
 * This implementation is pure and platform-independent: same input → same output,
 * no I/O, no state outside the input/output values.
 */

export type Rating = "again" | "hard" | "good" | "easy";

/**
 * Origin of a review record. Set by the route that wrote it so downstream
 * filters (e.g. coach plan's Filter C) can distinguish curriculum reinforcement
 * from free practice and challenge attempts.
 */
export type ReviewSource = "practice" | "free-drill" | "challenge";

/**
 * Map an exercise round's mode flags to the `ReviewSource` it should record.
 * Centralised so all exercise routes follow the same policy:
 *   challenge mode wins; otherwise fsrsEnabled distinguishes
 *   "practice" (schedule-affecting review) from "free-drill" (no schedule effect).
 * Note: ReviewSource values are DB identifiers — kept stable to avoid migration.
 */
export function exerciseReviewSource(args: {
  challengeMode: boolean;
  fsrsEnabled: boolean;
}): ReviewSource {
  if (args.challengeMode) return "challenge";
  return args.fsrsEnabled ? "practice" : "free-drill";
}

export type CardState = "new" | "learning" | "review" | "relearning";

export interface Card {
  /** Stability in days. Higher = the memory lasts longer. 0 for new cards. */
  stability: number;
  /** Difficulty 1..10. Higher = harder for this user. 0 for new cards. */
  difficulty: number;
  /** Number of times reviewed so far. */
  reps: number;
  /** Number of times "again" was hit. */
  lapses: number;
  /** Current state. */
  state: CardState;
  /** ISO timestamp of the last review, or null if never reviewed. */
  lastReview: string | null;
  /** ISO timestamp of when this card is next due. */
  due: string;
}

export interface FsrsParams {
  /**
   * Weights — 19 floats that parameterize the algorithm. These are the
   * default FSRS-4.5 weights, optimized over millions of Anki reviews.
   * Per-user optimization is possible later but the defaults are excellent
   * out of the box.
   */
  w: readonly number[];
  /**
   * Desired retention probability (0..1). 0.9 means "schedule the next review
   * for when I'd have a 90% chance of remembering". Higher = more frequent reviews,
   * higher accuracy. Lower = fewer reviews, more forgetting.
   */
  requestRetention: number;
  /** Maximum interval in days. Caps long-term scheduling. */
  maximumInterval: number;
}

/** Default FSRS-4.5 parameters. */
export const DEFAULT_PARAMS: FsrsParams = {
  w: [
    0.4072, 1.1829, 3.1262, 15.4722,
    7.2102, 0.5316, 1.0651, 0.0234,
    1.616, 0.1544, 1.0824, 1.9813,
    0.0953, 0.2975, 2.2042, 0.2407,
    2.9466, 0.5034, 0.6567,
  ],
  requestRetention: 0.9,
  maximumInterval: 36500, // ~100 years
};

/** Construct a brand-new card. Due immediately. */
export function newCard(now: Date = new Date()): Card {
  return {
    stability: 0,
    difficulty: 0,
    reps: 0,
    lapses: 0,
    state: "new",
    lastReview: null,
    due: now.toISOString(),
  };
}

/** Map rating to numeric grade for the formulas. */
function gradeOf(rating: Rating): number {
  return { again: 1, hard: 2, good: 3, easy: 4 }[rating];
}

/** Initial difficulty for a freshly-graded new card. */
function initialDifficulty(rating: Rating, params: FsrsParams): number {
  const w = params.w;
  const grade = gradeOf(rating);
  const d = w[4]! - Math.exp(w[5]! * (grade - 1)) + 1;
  return clamp(d, 1, 10);
}

/** Initial stability for a freshly-graded new card. */
function initialStability(rating: Rating, params: FsrsParams): number {
  const idx = gradeOf(rating) - 1;
  return Math.max(params.w[idx]!, 0.1);
}

/** Update difficulty after a review. */
function nextDifficulty(currentD: number, rating: Rating, params: FsrsParams): number {
  const w = params.w;
  const grade = gradeOf(rating);
  // Deviation from "good" (grade 3) drives difficulty change.
  const deltaD = -w[6]! * (grade - 3);
  const newD = currentD + deltaD * (10 - currentD) / 9;
  // Mean-reversion toward initial "easy" difficulty to prevent runaway hardness.
  const initEasyD = w[4]! - Math.exp(w[5]! * 3) + 1;
  const reverted = w[7]! * initEasyD + (1 - w[7]!) * newD;
  return clamp(reverted, 1, 10);
}

/** Retrievability — probability of recall given current stability and elapsed days. */
export function retrievability(stability: number, elapsedDays: number): number {
  if (stability <= 0) return 0;
  // FSRS forgetting curve: power function with exponent -0.5
  const factor = Math.pow(0.9, 1 / -0.5) - 1; // ≈ 19/81
  return Math.pow(1 + factor * elapsedDays / stability, -0.5);
}

/** New stability after a successful review (rating ≥ "hard"). */
function nextStabilitySuccess(
  d: number, s: number, r: number, rating: Rating, params: FsrsParams,
): number {
  const w = params.w;
  const hardPenalty = rating === "hard" ? w[15]! : 1;
  const easyBonus = rating === "easy" ? w[16]! : 1;
  const sIncrease =
    Math.exp(w[8]!) *
    (11 - d) *
    Math.pow(s, -w[9]!) *
    (Math.exp(w[10]! * (1 - r)) - 1) *
    hardPenalty *
    easyBonus;
  return s * (1 + sIncrease);
}

/** New stability after a lapse (rating = "again"). */
function nextStabilityLapse(d: number, s: number, r: number, params: FsrsParams): number {
  const w = params.w;
  return (
    w[11]! *
    Math.pow(d, -w[12]!) *
    (Math.pow(s + 1, w[13]!) - 1) *
    Math.exp(w[14]! * (1 - r))
  );
}

/** Schedule next review based on stability and desired retention. */
function intervalDays(stability: number, params: FsrsParams): number {
  // Solve R(t) = requestRetention for t using the 0.9-anchored forgetting curve:
  //   R(t) = (1 + baseFactor * t/S)^(-0.5),  where baseFactor = 0.9^(-2) - 1
  // → t = S * (requestRetention^(-2) - 1) / baseFactor
  //
  // The base anchor 0.9 is intentional: stability S is *defined* as the time
  // at which R = 0.9, so requestRetention = 0.9 always gives interval = S.
  const baseFactor = Math.pow(0.9, -2) - 1; // ≈ 0.2346
  const targetFactor = Math.pow(params.requestRetention, -2) - 1;
  const interval = stability * targetFactor / baseFactor;
  return clamp(Math.round(interval), 1, params.maximumInterval);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

/**
 * Apply a rating to a card and return the updated card.
 * This is the single function the rest of the app calls after every exercise answer.
 */
export function review(
  card: Card,
  rating: Rating,
  now: Date = new Date(),
  params: FsrsParams = DEFAULT_PARAMS,
): Card {
  const nowIso = now.toISOString();

  // First-ever review: initialize stability and difficulty from the first rating.
  if (card.state === "new") {
    const stability = initialStability(rating, params);
    const difficulty = initialDifficulty(rating, params);
    const days = rating === "again" ? 1 : intervalDays(stability, params);
    return {
      stability,
      difficulty,
      reps: 1,
      lapses: rating === "again" ? 1 : 0,
      state: rating === "again" ? "learning" : "review",
      lastReview: nowIso,
      due: addDays(now, days).toISOString(),
    };
  }

  // Subsequent review: compute retrievability based on time since last review.
  const lastReview = new Date(card.lastReview!);
  const elapsedDays = Math.max((now.getTime() - lastReview.getTime()) / 86_400_000, 0);
  const r = retrievability(card.stability, elapsedDays);

  const newDifficulty = nextDifficulty(card.difficulty, rating, params);
  let newStability: number;
  let newState: CardState;
  let newLapses = card.lapses;

  if (rating === "again") {
    newStability = nextStabilityLapse(card.difficulty, card.stability, r, params);
    newState = "relearning";
    newLapses += 1;
  } else {
    newStability = nextStabilitySuccess(card.difficulty, card.stability, r, rating, params);
    newState = "review";
  }

  const days = rating === "again" ? 1 : intervalDays(newStability, params);

  return {
    stability: newStability,
    difficulty: newDifficulty,
    reps: card.reps + 1,
    lapses: newLapses,
    state: newState,
    lastReview: nowIso,
    due: addDays(now, days).toISOString(),
  };
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 86_400_000);
}

/**
 * Helper: compute current retrievability of a card right now.
 * Useful for sorting "most-likely-to-be-forgotten" first.
 */
export function currentRetrievability(card: Card, now: Date = new Date()): number {
  if (card.state === "new" || card.lastReview === null) return 0;
  const elapsedDays = (now.getTime() - new Date(card.lastReview).getTime()) / 86_400_000;
  return retrievability(card.stability, elapsedDays);
}

/** Whether a card is due for review at the given time. */
export function isDue(card: Card, now: Date = new Date()): boolean {
  return new Date(card.due).getTime() <= now.getTime();
}

/**
 * Convert a binary correct/incorrect plus optional response time into an FSRS rating.
 * This is what the exercise UI calls — it doesn't need to ask the user "how hard was that?"
 * the way Anki does, because for ear training we have objective right/wrong + timing.
 *
 * Heuristic:
 *   - incorrect → "again"
 *   - correct + slow (> slowMs) → "hard"
 *   - correct + normal → "good"
 *   - correct + very fast (< fastMs) AND high confidence streak → "easy"
 */
export function ratingFromResponse(
  correct: boolean,
  responseTimeMs: number,
  opts: { fastMs?: number; slowMs?: number } = {},
): Rating {
  const fastMs = opts.fastMs ?? 1500;
  const slowMs = opts.slowMs ?? 6000;
  if (!correct) return "again";
  if (responseTimeMs < fastMs) return "easy";
  if (responseTimeMs > slowMs) return "hard";
  return "good";
}

/* ─── Forgetting curve ───────────────────────────────────────────────── */

/**
 * One data point in a card's review history.
 * `stability` is the value **after** this review was applied.
 */
export interface ReviewPoint {
  timestamp: string;
  correct: boolean;
  stability: number;
  chosenAnswerId?: string | null;
}

/** Minimal review input — structurally compatible with ReviewRecord. */
interface ReviewHistoryInput {
  timestamp: string;
  rating: Rating;
  correct: boolean;
  itemId: string;
  chosenAnswerId?: string | null;
}

/**
 * Replay a card's review history to compute the resulting stability after
 * each review. Input order doesn't matter — reviews are sorted internally.
 */
export function buildReviewPoints(reviews: ReviewHistoryInput[]): ReviewPoint[] {
  if (reviews.length === 0) return [];
  const sorted = [...reviews].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  let card = newCard(new Date(sorted[0]!.timestamp));
  const points: ReviewPoint[] = [];
  for (const r of sorted) {
    card = review(card, r.rating, new Date(r.timestamp));
    points.push({
      timestamp: r.timestamp,
      correct: r.correct,
      stability: card.stability,
      chosenAnswerId: r.chosenAnswerId,
    });
  }
  return points;
}

/**
 * Compute forgetting curve (x, y) pairs for an item's review history.
 *
 * Each review starts a new decay segment at R=1.0. The curve decays from
 * that point until the next review or `horizonDays` into the future.
 * `dayOffset` is days since the first review.
 *
 * Returns points suitable for an SVG polyline. Adjacent segments are not
 * connected (the curve "resets" at each review), so callers should split
 * on R=1.0 transitions when drawing.
 */
export function forgettingCurvePoints(
  reviews: ReviewPoint[],
  horizonDays = 30,
): { dayOffset: number; retrievability: number }[] {
  if (reviews.length === 0) return [];
  const sorted = [...reviews].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  const originMs = new Date(sorted[0]!.timestamp).getTime();
  const out: { dayOffset: number; retrievability: number }[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const r = sorted[i]!;
    const reviewMs = new Date(r.timestamp).getTime();
    const reviewDayOffset = (reviewMs - originMs) / 86400000;
    const endMs =
      i + 1 < sorted.length
        ? new Date(sorted[i + 1]!.timestamp).getTime()
        : reviewMs + horizonDays * 86400000;
    const segmentDays = Math.max(0, (endMs - reviewMs) / 86400000);
    const numSteps = Math.max(2, Math.min(60, Math.ceil(segmentDays)));
    for (let step = 0; step <= numSteps; step++) {
      const elapsed = segmentDays * (step / numSteps);
      out.push({
        dayOffset: reviewDayOffset + elapsed,
        retrievability: retrievability(r.stability, elapsed),
      });
    }
  }
  return out;
}
